import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Menu,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { toast, ToastContainer } from "react-toastify";
import { listCourses } from "../../api/assessmentHierarchy";
import { listStudents, STUDENT_STATUSES, apiErrorMessage } from "../../api/assessmentStudents";
import { listCategories } from "../../api/assessmentCategories";
import useDebouncedValue from "./useDebouncedValue";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
const ACCENT = "#17479e";

// Columns with a `sortKey` are sortable server-side (the backend sorts by
// avgScorePercentage, testsAttempted, lastActiveAt and studentName).
const allColumns = [
  { key: "id", label: "ID", cellCss: { minWidth: "60px", width: "60px" }, getValue: (r) => r.studentUserId },
  { key: "name", label: "Name", sortKey: "studentName", cellCss: { align: "left", textAlign: "left", minWidth: "200px" }, getValue: (r) => r.studentName },
  { key: "email", label: "Email", cellCss: { minWidth: "220px" }, getValue: (r) => r.email },
  { key: "phone", label: "Phone", cellCss: { minWidth: "130px" }, getValue: (r) => r.phoneNumber },
  { key: "course", label: "Exam", cellCss: { minWidth: "180px" }, getValue: (r) => (r.courseNames || []).join(", ") },
  { key: "category", label: "Course", cellCss: { minWidth: "200px" }, getValue: (r) => (r.categoryNames || []).join(", ") },
  { key: "testsAttempted", label: "Tests Attempted", sortKey: "testsAttempted", cellCss: { align: "center", minWidth: "140px" }, getValue: (r) => r.testsAttempted },
  { key: "avgScore", label: "Avg Score", sortKey: "avgScorePercentage", cellCss: { align: "center", minWidth: "110px" }, getValue: (r) => r.avgScorePercentage },
  { key: "completionRate", label: "Completion Rate", cellCss: { align: "center", minWidth: "140px" }, getValue: (r) => r.completionRate },
  { key: "lastActive", label: "Last Active", sortKey: "lastActiveAt", cellCss: { minWidth: "170px" }, getValue: (r) => r.lastActiveAt },
  { key: "status", label: "Status", cellCss: { align: "center", minWidth: "110px" }, getValue: (r) => r.status },
];

const defaultVisibleKeys = new Set(allColumns.map((c) => c.key));
// Stores the HIDDEN column keys so newly added columns show by default.
const STORAGE_KEY = "assessmentStudentsHiddenColumns.v2";

const statusCfg = {
  ACTIVE: { label: "Active", color: "#2e7d32", bg: "#e8f5e9" },
  EXPIRED: { label: "Expired", color: "#b26a00", bg: "#fff4e0" },
  PENDING: { label: "Pending", color: "#1565c0", bg: "#e3f0fd" },
  FAILED: { label: "Failed", color: "#c62828", bg: "#fdecea" },
  REFUNDED: { label: "Refunded", color: "#6a1b9a", bg: "#f3e5f5" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// avgScorePercentage / completionRate arrive as percentage numbers (0-100).
const formatPercent = (value) =>
  value == null || Number.isNaN(Number(value)) ? "—" : `${Math.round(Number(value) * 10) / 10}%`;

const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

function AssessmentStudents() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [courseFilter, setCourseFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Default: most recently active first.
  const [orderBy, setOrderBy] = useState("lastActiveAt");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const hidden = JSON.parse(raw);
        if (Array.isArray(hidden)) {
          const visible = allColumns.map((c) => c.key).filter((k) => !hidden.includes(k));
          if (visible.length > 0) return new Set(visible);
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  useEffect(() => {
    listCourses({ includeInactive: true })
      .then(setCourses)
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load exams")));
  }, []);

  useEffect(() => {
    listCategories({ courseId: courseFilter || undefined, size: 100 })
      .then((data) => setCategories(data.content || []))
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load courses")));
  }, [courseFilter]);

  useEffect(() => {
    setLoading(true);
    listStudents({
      courseId: courseFilter,
      categoryId: categoryFilter,
      status: statusFilter,
      search: debouncedSearch,
      page,
      size: rowsPerPage,
      sortBy: orderBy,
      sortDir: order,
    })
      .then((data) => {
        setStudents(data.content || []);
        setTotalElements(data.totalElements ?? 0);
      })
      .catch((err) => {
        setStudents([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load students"));
      })
      .finally(() => setLoading(false));
  }, [courseFilter, categoryFilter, statusFilter, debouncedSearch, page, rowsPerPage, orderBy, order]);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(allColumns.map((c) => c.key).filter((k) => !visibleColumns.has(k))),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));

  const handleSort = (sortKey) => {
    const isAsc = orderBy === sortKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(sortKey);
    setPage(0);
  };

  const handleCourseFilter = (value) => {
    setCourseFilter(value);
    setCategoryFilter("");
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Performance is per course: use the course filter when set, otherwise the
  // student's first course (the performance page has its own course selector).
  const openPerformance = (row) => {
    // A selected category carries its own course: use it so the course/category pair always
    // matches (the backend rejects a category that belongs to a different course).
    const selectedCategory = categories.find((c) => String(c.id) === String(categoryFilter));
    const courseId = selectedCategory?.courseId || courseFilter || row.courseIds?.[0];
    if (!courseId) {
      toast.info("This student has no exam to show performance for");
      return;
    }
    const categoryQuery = selectedCategory ? `&categoryId=${selectedCategory.id}` : "";
    navigate(
      `/assessment/students/${row.studentUserId}/performance?courseId=${courseId}${categoryQuery}`,
    );
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: "1 1 560px", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search by name, email or phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 260, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Exam</InputLabel>
                <Select label="Exam" value={courseFilter} onChange={(e) => handleCourseFilter(e.target.value)}>
                  <MenuItem value="">All Exams</MenuItem>
                  {courses.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Course</InputLabel>
                <Select
                  label="Course"
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All Courses</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {STUDENT_STATUSES.map((st) => (
                    <MenuItem key={st} value={st}>
                      {statusCfg[st].label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
                {totalElements} students
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenColumnsMenu}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Columns
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={openColumnsMenu}
                onClose={handleCloseColumnsMenu}
                PaperProps={{ sx: { width: 200, maxHeight: 500, overflowY: "auto", borderRadius: 2 } }}
              >
                {allColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={<Checkbox checked={visibleColumns.has(col.key)} onChange={() => toggleColumn(col.key)} />}
                    label={col.label}
                    sx={{ pl: 1, pr: 2 }}
                  />
                ))}
              </Menu>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2, borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)", minHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.cellCss?.align}
                    sx={{
                      ...c.cellCss,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                    sortDirection={c.sortKey && orderBy === c.sortKey ? order : false}
                  >
                    {c.sortKey ? (
                      <TableSortLabel
                        active={orderBy === c.sortKey}
                        direction={orderBy === c.sortKey ? order : "asc"}
                        onClick={() => handleSort(c.sortKey)}
                        sx={{
                          color: "#ffffff !important",
                          "&.Mui-active": { color: "#ffffff !important" },
                          "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                        }}
                      >
                        {c.label}
                      </TableSortLabel>
                    ) : (
                      c.label
                    )}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    minWidth: "140px",
                    padding: "10px 12px",
                    backgroundColor: ACCENT,
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton columns={visibleColumnDefs.length + 1} />
              ) : students.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length + 1} sx={{ textAlign: "center", py: 4 }}>
                    <AssessmentEmptyState message="No students found" hint="Try changing your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                students.map((row) => (
                  <TableRow
                    key={row.studentUserId}
                    hover
                    sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}
                  >
                    {visibleColumnDefs.map((c) => (
                      <TableCell key={c.key} align={c.cellCss?.align} sx={{ padding: "10px 12px", ...c.cellCss }}>
                        {(() => {
                          const value = c.getValue(row);
                          if (c.key === "name") {
                            return (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                                <Avatar sx={{ width: 28, height: 28, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#e8f0fe", color: ACCENT }}>
                                  {initials(row.studentName)}
                                </Avatar>
                                <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{value || "—"}</Typography>
                              </Box>
                            );
                          }
                          if (c.key === "avgScore" || c.key === "completionRate") return formatPercent(value);
                          if (c.key === "lastActive") return formatDateTime(value);
                          if (c.key === "status") return <StatusChip value={value} />;
                          if (c.key === "course" || c.key === "category") return value || "—";
                          return value ?? "—";
                        })()}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ padding: "10px 12px", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}
                    >
                      <Tooltip title="View Performance" arrow placement="top">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openPerformance(row)}
                          sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                        >
                          <InsightsOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
          }}
        />
      </Paper>

    </Box>
  );
}

export default AssessmentStudents;
